import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { supabase, TIER_LABEL } from '@/lib/supabase'
import { useAuth } from '@/auth/useAuth'
import { Badge, Card, PageHeader } from '@/components/ui'
import { FEATURES } from '@/lib/features'

export function Dashboard() {
  const { profile, isKtm, hasPortalAccess } = useAuth()
  const org = profile?.organisations

  const { data: announcements } = useQuery({
    queryKey: ['announcements'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('announcements')
        .select('id, title, body, published_at')
        .order('published_at', { ascending: false })
        .limit(3)
      if (error) throw error
      return data
    },
  })

  const firstName = (profile?.full_name ?? '').split(' ')[0]

  return (
    <>
      <PageHeader title={firstName ? `Kia ora, ${firstName}` : 'Kia ora'}>
        {isKtm
          ? 'Administration for the partner portal, the resource library and the shared evaluation set.'
          : FEATURES.evaluation
            ? 'Your resources, your training and your evaluation data, in one place.'
            : 'Your resources and training, in one place.'}
      </PageHeader>

      {org && (
        <Card className="mb-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="ref">Subscription</p>
              <p className="mt-1 text-base text-ink">
                {org.name} · {TIER_LABEL[org.tier]}
              </p>
            </div>
            <div className="text-right text-sm text-inksoft">
              <Badge tone={hasPortalAccess ? 'good' : 'alert'}>
                {org.subscription_status === 'active'
                  ? 'Active'
                  : org.subscription_status === 'grace'
                    ? 'Renewal overdue'
                    : 'Not active'}
              </Badge>
              {org.renews_at && (
                <p className="mt-1">
                  Renews {new Date(org.renews_at).toLocaleDateString('en-NZ')}
                </p>
              )}
            </div>
          </div>
        </Card>
      )}

      {(announcements ?? []).length > 0 && (
        <section className="mb-6">
          <h2 className="mb-3 text-base text-ink">Latest from Ki Tua o Matariki</h2>
          <div className="space-y-3">
            {(announcements ?? []).map((a) => (
              <Card key={a.id}>
                <h3 className="text-sm font-semibold text-ink">{a.title}</h3>
                <p className="mt-1 text-sm text-inksoft">{a.body}</p>
              </Card>
            ))}
          </div>
        </section>
      )}

      <section className="grid gap-4 sm:grid-cols-3">
        {isKtm ? (
          <>
            <QuickLink to="/admin/organisations" title="Partner organisations" body="Tiers, subscriptions and access." tone="teal" />
            <QuickLink to="/admin/resources" title="Resource library" body="Publish, version and gate resources." tone="lavender" />
            <QuickLink to="/admin/reporting" title="Aggregate reporting" body="The cross-partner impact picture." tone="peach" />
          </>
        ) : (
          <>
            <QuickLink to="/library" title="Resources" body="Toolkits, templates and training." tone="teal" />
            {FEATURES.evaluation && (
              <QuickLink to="/evaluation" title="Record a session" body="Enter cohort and participant data." tone="lavender" />
            )}
            {FEATURES.reports && (
              <QuickLink to="/reports" title="Your results" body="Your own reporting, ready to export." tone="peach" />
            )}
          </>
        )}
      </section>
    </>
  )
}

/** Primary-palette colour blocks, as in the brand guide's iconography cards. */
const QUICK_LINK_TONE = {
  teal: 'bg-teal',
  lavender: 'bg-lavender',
  peach: 'bg-peach',
} as const

function QuickLink({
  to,
  title,
  body,
  tone,
}: {
  to: string
  title: string
  body: string
  tone: keyof typeof QUICK_LINK_TONE
}) {
  return (
    <Link
      to={to}
      className={`rounded-2xl p-6 text-ink ring-pounamu transition-shadow hover:ring-2 ${QUICK_LINK_TONE[tone]}`}
    >
      <h3 className="text-base font-semibold">{title}</h3>
      <p className="mt-1 text-sm">{body}</p>
    </Link>
  )
}
